import { createTheme } from "@mui/material";
import type { Theme, Components } from "@mui/material/styles";
import type { AppContext } from "../../../types";

const components = (theme: Theme): Components<Theme> => {
    const { error, divider, action, primary, text } = theme.palette;
    return {
        MuiCssBaseline: {
            styleOverrides: {
                "*": {
                    margin: 0,
                    padding: 0,
                    boxSizing: `border-box`,
                },
                html: {
                    height: `100%`,
                    WebkitOverflowScrolling: `touch`,
                    MozOsxFontSmoothing: `grayscale`,
                    WebkitFontSmoothing: `antialiased`,
                },
                body: {
                    height: `100%`,
                },
                a: {
                    textDecoration: `none`,
                    color: primary.main,
                },
                input: {
                    "&[type=number]": {
                        MozAppearance: `textfield`,
                        "&::-webkit-outer-spin-button": {
                            margin: 0,
                            WebkitAppearance: `none`,
                        },
                        "&::-webkit-inner-spin-button": {
                            margin: 0,
                            WebkitAppearance: `none`,
                        },
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    boxShadow: `none`,
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    "min-width": `85px !important`,
                },
                colorSuccess: {
                    backgroundColor: `#11B88626`,
                    color: `#216e4e`,
                },
                colorError: {
                    backgroundColor: `#33333314`,
                    color: `#333`,
                },
                colorInfo: {
                    backgroundColor: `#0288D126`,
                    color: `#212e6e`,
                },
            },
        },
        MuiSwitch: {
            styleOverrides: {
                thumb: {
                    margin: `2px`,
                    width: `16px`,
                    height: `16px`,
                },
                track: {
                    height: `22px`,
                    borderRadius: `16px`,
                },
                root: { padding: `8px` },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: `none`,
                    borderRadius: `4px`,
                    color: `inherit`,
                    boxShadow: `none`,
                    padding: `0.6rem 1.5rem`,
                    fontSize: 15,
                    fontWeight: 600,
                    minWidth: 84,
                    "&:hover": {
                        backgroundColor: `${theme.palette.account.accent}CC`,
                    },
                    "&.Mui-disabled": {
                        backgroundColor: action.disabledBackground,
                        color: text.disabled,
                    },
                    "&.MuiButton-containedError": {
                        backgroundColor: error.main,
                        color: `white`,
                        boxShadow: `none`,
                    },
                    "&.MuiButton-containedSuccess": {
                        color: `white`,
                    },
                    "& .MuiButton-startIcon .MuiSvgIcon-root": {
                        fontSize: 16,
                    },
                },
                outlinedPrimary: {
                    borderColor: divider,
                    color: text.primary,
                },
                containedPrimary: {
                    color: `white`,
                    "&:hover": {
                        boxShadow: `none`,
                    },
                },
            },
            variants: [
                {
                    props: {
                        variant: `GreyOutlined`,
                    },
                    style: {
                        border: `1px solid ${divider}`,
                        color: text.primary,
                        "&:hover": {
                            backgroundColor: `transparent`,
                        },
                    },
                },
                {
                    props: {
                        variant: `GreyOutlined`,
                        size: `small`,
                    },
                    style: {
                        padding: `0.3rem 0.8rem`,
                    },
                },
                {
                    props: {
                        variant: `text`,
                    },
                    style: {
                        padding: 0,
                        "&:hover": {
                            backgroundColor: `transparent`,
                        },
                    },
                },
                {
                    props: {
                        variant: `white`,
                    },
                    style: {
                        padding: `12px 24px`,
                        backgroundColor: `white`,
                        color: text.disabled,
                    },
                },
                {
                    props: {
                        variant: `dashed`,
                    },
                    style: {
                        height: 36,
                        padding: 0,
                        minWidth: 36,
                        maxWidth: 36,
                        display: `flex`,
                        alignItems: `center`,
                        justifyContent: `center`,
                        border: `1px dashed ${divider}`,
                        borderRadius: `50%`,
                        overflow: `clip`,
                    },
                },
            ],
        },

        MuiTableHead: {
            styleOverrides: {
                root: {
                    borderColor: `transparent`,
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    "&.Mui-selected:hover": {
                        backgroundColor: `transparent`,
                    },
                },
            },
        },
        MuiTableBody: {
            styleOverrides: {
                root: {
                    "& .MuiTableRow-root:last-of-type": {
                        "& .MuiTableCell-root": {
                            borderBottom: 0,
                            color: `#333`,
                        },
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    fontSize: 15,
                    fontWeight: 500,
                    color: `#333`,
                    borderBottom: 0,
                    paddingRight: `24px`,
                    paddingLeft: `24px`,
                    "&.MuiTableCell-head": {
                        paddingRight: 0,
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                input: {
                    "&::placeholder": {
                        opacity: 0.86,
                        color: `#42526e`,
                    },
                },
            },
        },
        MuiTextField: {
            variants: [
                {
                    props: {
                        variant: `outlined` as const,
                        color: `grey`,
                    } as Record<string, string>,
                    style: {
                        "& .MuiOutlinedInput-input": {
                            fontSize: 15,
                            fontWeight: 500,
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                            border: `2px solid ${divider}`,
                        },
                        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                            border: `2px solid ${divider}`,
                        },
                        "& input::placeholder": {
                            color: text.disabled,
                        },
                    },
                },
            ],
        },
        MuiSvgIcon: {
            styleOverrides: {
                root: {
                    "& .secondary": {
                        opacity: 0.4,
                    },
                },
            },
        },
    };
};

export const createCustomTheme = ({ context }: { context: AppContext | undefined }): Theme => {
    const theme = createTheme({
        typography: {
            fontFamily: `Lato`,
        },
        spacing: 8,
        breakpoints: {
            values: { xs: 0, sm: 614, md: 1250 },
        },
        palette: {
            success: {
                25: `#F1FEF5`,
                50: `#E3FDEB`,
                100: `#CDFBDB`,
                200: `#9DF7C2`,
                300: `#6AE9AA`,
                400: `#43D49A`,
                500: `#11b886`,
                600: `#0C9E80`,
                700: `#088477`,
                800: `#056A6A`,
                900: `#035058`,
                main: `#11b886`,
            },
            warning: {
                25: `#FFFCF5`,
                50: `#FFF8E`,
                100: `#FFF8E6`,
                200: `#FFEBB3`,
                300: `#FEDE80`,
                400: `#FED14D`,
                500: `#FEBF06`,
                600: `#DB7E24`,
                700: `#B75F19`,
                800: `#93440F`,
                900: `#7A3109`,
                main: `#FEBF06`,
            },
            error: {
                main: `#cc1919`,
            },
            account: {
                primary: context?.adminConfig?.mall?.main_color,
                background: `rgb(0 0 0 / 3%)`,
                whiteboxBorder: `1px solid #E1E1E1`,
                accent: context?.adminConfig?.mall?.accent_color,
                accentGradientEnd: context?.adminConfig?.mall?.accent_gradient_end,
                activeBackground: `#f6f7f8`,
                outerBorder: `1px solid #c3c4c6`,
                innerBorder: `1px solid #e5e7eb`,
                text: {
                    primary: `#333`,
                },
            },
        },
    }); // set shadows

    (theme as { components: Components<Theme> }).components = components(theme);

    return theme;
};
